import React, { useEffect } from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram } from 'lucide-react';
import { companyInfo } from '../mock';
import { ContactForm } from '../components/ContactForm';

const Contact = () => {
  useEffect(() => {
    // Set page-specific meta tags
    document.title = 'Kontakt Fryd! - Få et tilbud på dit projekt | Tømrer & Snedkerfirma';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Kontakt Fryd! for et uforpligtende tilbud. Vi realiserer arkitektens vision med kompromisløst håndværk i træ. Ring eller skriv til os i dag.');
    }
    
    // Set canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://ffryd.dk/kontakt');
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 pt-32 pb-20 px-8 md:px-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-20">
          <h1 className="text-5xl md:text-7xl font-light text-gray-900 mb-8">
            Kontakt
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-20 max-w-7xl">
          {/* Contact Information */}
          <div>
            <h2 className="text-3xl font-light text-gray-900 mb-12">
              {companyInfo.name}
            </h2>
            
            <div className="space-y-8">
              {/* Owner */}
              <div>
                <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Ejer</p>
                <p className="text-gray-900 text-xl font-light">{companyInfo.contact.owner}</p>
              </div>

              {/* Address */}
              <div>
                <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Adresse</p>
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-gray-400 mt-1" />
                  <div className="text-gray-900 text-xl font-light">
                    <p>{companyInfo.contact.address}</p>
                    <p>{companyInfo.contact.city}</p>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div>
                <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Telefon</p>
                <a
                  href={`tel:${companyInfo.contact.phone.replace(/\s/g, '')}`}
                  className="flex items-center space-x-3 text-gray-900 text-xl font-light hover:text-gray-600 transition-colors duration-300"
                >
                  <Phone className="w-5 h-5 text-gray-400" />
                  <span>{companyInfo.contact.phone}</span>
                </a>
              </div>

              {/* Email */}
              <div>
                <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">Email</p>
                <a
                  href={`mailto:${companyInfo.contact.email}`}
                  className="flex items-center space-x-3 text-gray-900 text-xl font-light hover:text-gray-600 transition-colors duration-300"
                >
                  <Mail className="w-5 h-5 text-gray-400" />
                  <span>{companyInfo.contact.email}</span>
                </a>
              </div>

              {/* CVR */}
              <div>
                <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">CVR-nr.</p>
                <p className="text-gray-900 text-xl font-light">{companyInfo.contact.cvr}</p>
              </div>
            </div>

            {/* Social Media - Without LinkedIn */}
            <div className="mt-12">
              <p className="text-gray-500 text-sm mb-4 uppercase tracking-wide">Find os på</p>
              <div className="flex space-x-4">
                <a
                  href={companyInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-300"
                  aria-label="Facebook"
                >
                  <Facebook className="w-5 h-5 text-gray-700" />
                </a>
                <a
                  href={companyInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-300"
                  aria-label="Instagram"
                >
                  <Instagram className="w-5 h-5 text-gray-700" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Value Propositions */}
          <div className="space-y-10">
            <div>
              <h3 className="text-xl font-light text-gray-900 mb-3">
                Vi realiserer arkitektens vision - præcist som den er tænkt og tegnet
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Vi arbejder udelukkende med arkitekter og taler deres sprog. Vi oversætter tegninger og idéer til virkelighed - uden forfladigelse eller kompromiser. Vi ved, hvad der er vigtigt – og hvorfor.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-light text-gray-900 mb-3">
                Materialer og løsninger, der forstærker idéen
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Vi rådgiver i materialer, detaljer og løsninger, der styrker helheden og respekterer visionen. Vi foreslår med blik for æstetik, funktion og budget – altid med arkitektens intention i fokus.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-light text-gray-900 mb-3">
                Kompromisløs kvalitet i udførelsen
              </h3>
              <p className="text-gray-600 leading-relaxed">
                Alt bliver bygget med præcision, perfektion og passion. Vi er kendt for kompromisløst håndværk, hvor finish, detalje og helhed går op i en højere enhed.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-200">
              <h3 className="text-xl font-light text-gray-900 mb-3">
                Resultatet?
              </h3>
              <p className="text-gray-600 leading-relaxed">
                En bekymringsfri proces, hvor arkitekten bliver grebet, forstået og leveret et resultat - præcis som det var tænkt. Tro mod idéen. Helt ned i detaljen.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        {process.env.REACT_APP_CONTACT_FORM_ENABLED === 'true' && <section data-testid="contact-form-section" className="mt-32 pt-20 border-t border-gray-200 grid md:grid-cols-2 gap-20">
          <div>
            <h2 className="text-3xl font-light text-gray-900 mb-6">Fortæl os om dit projekt</h2>
            <p className="text-gray-600 leading-relaxed max-w-md">
              Beskriv kort projektet, så vender vi tilbage hurtigst muligt.
            </p>
          </div>
          <ContactForm />
        </section>}

        {/* Logo */}
        <div className="mt-20 flex justify-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_crafted-timber/artifacts/g34rxuid_Udklip.PNG" 
            alt="Fryd! logo" 
            className="w-32 h-32 opacity-10 grayscale"
          />
        </div>

        {/* Footer Note */}
        <div className="mt-8 pt-8 border-t border-gray-200">
          <p className="text-gray-400 text-sm text-center">
            © {new Date().getFullYear()} {companyInfo.name} · Alle rettigheder forbeholdes
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
